import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Icon from '../components/Icon';
import AdminShell from '../components/admin/AdminShell';
import AdminBulkInvoiceSmsSettingsPanel from '../components/views/admin/AdminBulkInvoiceSmsSettingsPanel';
import AdminUserProfileModal from '../components/admin/AdminUserProfileModal';
import { doNamesReluctantlyMatch, maskKraPin } from '../utils/kraNameMatch';
import { SkeletonAdminBody } from '../components/Skeleton';

// Constants
const BASE_MODULE_OPTIONS = ['FRP', 'Groups', 'UTBills', 'Vehicles'];
const MODULE_SORT_ORDER = {
  FRP: 0,
  Groups: 1,
  UTBills: 2,
  Vehicles: 3,
};
const MODULE_LABELS = {
  UTBills: 'UT Bills',
};
const FALLBACK_ROLE_OPTIONS = [
  { value: 'Mbr', label: 'Member' },
  { value: 'M', label: 'Management' },
  { value: 'L', label: 'Landlord' },
  { value: 'T', label: 'Tenant' },
];

const ROLE_LABELS = {
  A: 'Admin',
  M: 'Management',
  L: 'Landlord',
  T: 'Tenant',
  Mbr: 'Member',
};
const ROLE_FILTER_OPTIONS = [
  { value: 'all', label: 'All roles' },
  { value: 'A', label: 'Admins' },
  { value: 'M', label: 'Management' },
  { value: 'L', label: 'Landlords' },
  { value: 'T', label: 'Tenants' },
  { value: 'Mbr', label: 'Members' },
];

const STATUS_CONFIG = {
  locked: { label: 'Locked', className: 'bg-red-100 text-red-700' },
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
};

const MESSAGE_DURATION = 5000;
const ACTIVE_WINDOW_MS = 2 * 60 * 1000;
const MODULE_STYLE_CONFIG = {
  FRP: {
    active: 'border-emerald-300 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.95),_rgba(209,250,229,0.95)_42%,_rgba(167,243,208,0.9)_100%)] text-emerald-900 shadow-emerald-100',
    idle: 'border-emerald-200 bg-[linear-gradient(135deg,_rgba(240,253,244,1)_0%,_rgba(209,250,229,0.96)_52%,_rgba(236,253,245,1)_100%)] text-emerald-800 hover:border-emerald-300',
  },
  Groups: {
    active: 'border-sky-300 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.95),_rgba(224,242,254,0.95)_42%,_rgba(186,230,253,0.9)_100%)] text-sky-900 shadow-sky-100',
    idle: 'border-sky-200 bg-[linear-gradient(135deg,_rgba(240,249,255,1)_0%,_rgba(224,242,254,0.96)_52%,_rgba(236,254,255,1)_100%)] text-sky-800 hover:border-sky-300',
  },
  UTBills: {
    active: 'border-teal-300 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.95),_rgba(204,251,241,0.95)_42%,_rgba(153,246,228,0.9)_100%)] text-teal-900 shadow-teal-100',
    idle: 'border-teal-200 bg-[linear-gradient(135deg,_rgba(240,253,250,1)_0%,_rgba(204,251,241,0.96)_52%,_rgba(240,253,250,1)_100%)] text-teal-800 hover:border-teal-300',
  },
};

const getRoleLabel = (role) => ROLE_LABELS[role] || role || 'Unknown';
const getModuleLabel = (moduleName) => MODULE_LABELS[moduleName] || moduleName;
const LOGIN_DATE_FORMAT_OPTIONS = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
};

const formatLastLogin = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString('en-US', LOGIN_DATE_FORMAT_OPTIONS);
};
const formatRecentLogins = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((value) => formatLastLogin(value))
    .filter((value) => value !== 'Never')
    .slice(0, 5);
};
const getTimestampMs = (value) => {
  if (!value) return null;
  const date = new Date(value);
  const ms = date.getTime();
  return Number.isNaN(ms) ? null : ms;
};
const formatRelativeTime = (value) => {
  const timestamp = getTimestampMs(value);
  if (!timestamp) return 'Unknown time';

  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.max(0, Math.round(diffMs / (1000 * 60)));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr${diffHours === 1 ? '' : 's'} ago`;

  const diffDays = Math.round(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
};
const isUserRecentlyActive = (user) => {
  if (user?.locked || !user?.lastSeenAt) return false;
  const lastSeen = new Date(user.lastSeenAt);
  if (Number.isNaN(lastSeen.getTime())) return false;
  return Date.now() - lastSeen.getTime() <= ACTIVE_WINDOW_MS;
};
const isSameLocalDay = (value, referenceDate = new Date()) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return (
    date.getFullYear() === referenceDate.getFullYear() &&
    date.getMonth() === referenceDate.getMonth() &&
    date.getDate() === referenceDate.getDate()
  );
};

const sortModuleNames = (modules = []) =>
  [...modules].sort((a, b) => {
    const leftRank = MODULE_SORT_ORDER[a] ?? Number.MAX_SAFE_INTEGER;
    const rightRank = MODULE_SORT_ORDER[b] ?? Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return String(a || '').localeCompare(String(b || ''));
  });
const buildModuleSelectionState = (items = []) =>
  items.reduce((acc, user) => {
    acc[String(user?._id || '')] = Array.isArray(user?.assignedModules) ? user.assignedModules : [];
    return acc;
  }, {});

// Custom Hooks
const useAuth = (navigate) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const res = await api.get('/auth/me');
        const userData = res?.data;

        if (!userData?._id) {
          navigate('/login');
          return;
        }

        if (userData.role !== 'A') {
          navigate('/dashboard');
          return;
        }

        if (userData.locked) {
          navigate('/locked');
          return;
        }

        setCurrentUser(userData);
      } catch (fetchError) {
        console.error('Error fetching user:', fetchError);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [navigate]);

  return { currentUser, loading };
};

const useDirectoryData = (currentUser) => {
  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [moduleSelections, setModuleSelections] = useState({});
  const [fallbackRoles, setFallbackRoles] = useState({});

  useEffect(() => {
    if (!currentUser?._id) return;

    const fetchDirectory = async () => {
      try {
        setPageLoading(true);
        setError('');
        
        const [adminsRes, usersRes] = await Promise.all([
          api.get('/auth/admins'),
          api.get('/auth/admin/users'),
        ]);

        const adminList = Array.isArray(adminsRes?.data?.admins) ? adminsRes.data.admins : [];
        const userList = Array.isArray(usersRes?.data?.users) ? usersRes.data.users : [];

        setAdmins(adminList);
        setUsers(userList);
        
        setModuleSelections(() => {
          const next = {};
          userList.forEach((user) => {
            next[user._id] = user.assignedModules || [];
          });
          return next;
        });
        
        setFallbackRoles(() => {
          const next = {};
          userList.forEach((user) => {
            next[user._id] = user.role !== 'A' ? user.role : 'Mbr';
          });
          return next;
        });
      } catch (fetchError) {
        console.error('Error loading admin directory:', fetchError);
        setError(fetchError?.response?.data?.error || 'Failed to load admin management data');
      } finally {
        setPageLoading(false);
      }
    };

    fetchDirectory();
  }, [currentUser]);

  return {
    admins,
    users,
    pageLoading,
    error,
    moduleSelections,
    fallbackRoles,
    setAdmins,
    setUsers,
    setModuleSelections,
    setFallbackRoles,
    setError,
  };
};

// Components
const LoadingSpinner = () => (
  <div className="min-h-screen bg-slate-100">
    <SkeletonAdminBody rows={8} />
  </div>
);

const AlertMessage = ({ message, error, onDismiss }) => {
  if (!message && !error) return null;
  
  const isSuccess = !!message;
  return (
    <div
      className={`rounded-xl border px-4 py-3 text-sm shadow-sm ${
        isSuccess
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
          : 'border-red-200 bg-red-50 text-red-800'
      }`}
      role="alert"
    >
      <div className="flex items-center justify-between">
        <span>{message || error}</span>
        <button
          onClick={onDismiss}
          className="ml-4 text-current opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Dismiss"
        >
          <Icon name="x" size="1rem" />
        </button>
      </div>
    </div>
  );
};

// Excel-style table header component
const ExcelTableHeader = ({ children }) => (
  <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600 bg-[#f8fafc] border-b border-r border-slate-200 last:border-r-0">
    {children}
  </th>
);

const UserActions = ({
  user,
  selectedModules,
  moduleOptions,
  isAdmin,
  isSaving,
  fallbackRole,
  onEditProfile,
  onToggleModule,
  onSaveModules,
  onRetryKraVerification,
  onToggleKraMatchOverride,
  onPromote,
  onToggleMpesaReconciliationAccess,
  onToggleMpesaReconciliationRecoverAccess,
  onToggleC2bPaymentBlock,
  onRemoveAdmin,
  onFallbackRoleChange,
  adminsCount
}) => {
  const moduleCheckboxes = moduleOptions.map((moduleName) => {
    const checked = selectedModules.includes(moduleName);
    const tone = MODULE_STYLE_CONFIG[moduleName] || {
      active: 'border-blue-400 bg-blue-50 text-blue-800 shadow-blue-100',
      idle: 'border-slate-200 bg-slate-100 text-slate-700 hover:border-slate-300',
    };
    return (
      <label
        key={`${user._id}-${moduleName}`}
        className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors cursor-pointer ${
          checked
            ? `${tone.active} shadow-sm`
            : tone.idle
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggleModule(user._id, moduleName)}
          className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-200"
          disabled={isSaving}
        />
        <span className="font-medium">{getModuleLabel(moduleName)}</span>
      </label>
    );
  });

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/80 p-3 shadow-inner">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Module Access
        </div>
        <div className="flex flex-wrap gap-2">{moduleCheckboxes}</div>
      </div>
      <div className="flex flex-col gap-2 min-w-[160px]">
        <button
          type="button"
          onClick={() => onEditProfile(user)}
          disabled={isSaving}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition-all hover:bg-slate-50 hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          <Icon name="pencil-square" size="14px" />
          Edit Profile
        </button>

        <button
          type="button"
          onClick={() => onSaveModules(user)}
          disabled={isSaving}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition-all hover:bg-blue-100 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          <Icon name="save" size="14px" />
          {isSaving ? 'Saving...' : 'Update Modules'}
        </button>

        <button
          type="button"
          onClick={() => onRetryKraVerification(user)}
          disabled={isSaving}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition-all hover:bg-emerald-100 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          <Icon name="arrow-clockwise" size="14px" />
          {isSaving ? 'Verifying...' : 'Verify'}
        </button>

        {String(user?.kraPin || '').trim() && String(user?.kraTaxpayerName || '').trim() && !doNamesReluctantlyMatch(user?.name || '', user?.kraTaxpayerName || '') ? (
          <button
            type="button"
            onClick={() => onToggleKraMatchOverride(user)}
            disabled={isSaving}
            className={`inline-flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${
              user?.kraNameMatchOverridden
                ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                : 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'
            }`}
          >
            <Icon name={user?.kraNameMatchOverridden ? 'x-circle' : 'shield-check'} size="14px" />
            {user?.kraNameMatchOverridden ? 'Remove Match Override' : 'Mark as Match'}
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => onPromote(user)}
          disabled={isSaving || isAdmin}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-500/30 ring-1 ring-inset ring-white/20 transition-all hover:from-indigo-700 hover:to-violet-700 hover:shadow-indigo-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
        >
          <Icon name="person-plus" size="16px" />
          {isSaving && !isAdmin ? 'Processing...' : 'Make Admin'}
        </button>

        <div className="text-right text-[10px] leading-4 text-slate-400">
          {selectedModules.length > 0
            ? `${selectedModules.length} module${selectedModules.length === 1 ? '' : 's'} selected`
            : 'No modules selected'}
        </div>

        <button
          type="button"
          onClick={() => onToggleMpesaReconciliationAccess(user)}
          disabled={isSaving}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${
            user.canUseMpesaReconciliation
              ? 'border-transparent bg-sky-600 text-white hover:bg-sky-700'
              : 'border-dashed border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:border-slate-400'
          }`}
        >
          <Icon name={user.canUseMpesaReconciliation ? 'check-circle' : 'search'} size="14px" />
          {user.canUseMpesaReconciliation ? 'Search Enabled' : 'Enable Search'}
        </button>

        <button
          type="button"
          onClick={() => onToggleMpesaReconciliationRecoverAccess(user)}
          disabled={isSaving}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${
            user.canRecoverMpesaReconciliation
              ? 'border-transparent bg-amber-500 text-white hover:bg-amber-600'
              : 'border-dashed border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:border-slate-400'
          }`}
        >
          <Icon name={user.canRecoverMpesaReconciliation ? 'check-circle' : 'shield'} size="14px" />
          {user.canRecoverMpesaReconciliation ? 'Reconcile Enabled' : 'Enable Reconcile'}
        </button>

        <button
          type="button"
          onClick={() => onToggleC2bPaymentBlock(user)}
          disabled={isSaving}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${
            user.c2bPaymentBlocked
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
          }`}
        >
          <Icon name={user.c2bPaymentBlocked ? 'check-circle' : 'slash-circle'} size="14px" />
          {user.c2bPaymentBlocked ? 'Allow C2B Payments' : 'Block C2B Payments'}
        </button>

        {isAdmin ? (
          <>
            <select
              value={fallbackRole}
              onChange={(e) => onFallbackRoleChange(user._id, e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 shadow-sm"
              disabled={isSaving}
            >
              {FALLBACK_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  Demote to {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => onRemoveAdmin(user)}
              disabled={isSaving || adminsCount <= 1}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 transition-all hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <Icon name="person-dash" size="14px" />
              Remove Admin
            </button>
          </>
        ) : (
          <div className="w-full rounded-lg border border-dashed border-slate-200 px-3 py-2 text-center text-[10px] text-slate-400 bg-slate-50/50">
            Not an admin
          </div>
        )}
      </div>
    </div>
  );
};

const ModuleBadges = ({ modules = [] }) => (
  <div className="flex flex-wrap gap-1.5">
    {modules.length > 0 ? (
      modules.map((moduleName) => {
        const tone = MODULE_STYLE_CONFIG[moduleName]?.idle || 'border-slate-200 bg-slate-100 text-slate-700';
        return (
          <span
            key={moduleName}
            className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold shadow-sm ${tone}`}
          >
            {getModuleLabel(moduleName)}
          </span>
        );
      })
    ) : (
      <span className="text-xs italic text-slate-400">No modules assigned</span>
    )}
  </div>
);

// Legacy helpers retained to avoid touching existing encoded strings in-place.
// eslint-disable-next-line no-unused-vars
const renderAdminUserKraSummary = (user) => {
  const kraPin = String(user?.kraPin || '').trim();
  const kraTaxpayerName = String(user?.kraTaxpayerName || '').trim();
  const kraNameMatches = doNamesReluctantlyMatch(user?.name || '', kraTaxpayerName);

  if (kraPin) {
    return kraNameMatches
      ? `PIN: ${maskKraPin(kraPin)} • ${kraTaxpayerName || 'Taxpayer name unavailable'}`
      : `PIN: ${maskKraPin(kraPin)} • No match`;
  }

  if (user?.kraPinCheckedAt) {
    return 'User not verified';
  }

  return 'Verification pending';
};

// eslint-disable-next-line no-unused-vars
const hasAdminUserKraNameMismatch = (user) => {
  const kraPin = String(user?.kraPin || '').trim();
  const kraTaxpayerName = String(user?.kraTaxpayerName || '').trim();
  return !!kraPin && !doNamesReluctantlyMatch(user?.name || '', kraTaxpayerName);
};

// eslint-disable-next-line no-unused-vars
const getAdminUserKraStatus = (user) => {
  const kraPin = String(user?.kraPin || '').trim();
  const kraTaxpayerName = String(user?.kraTaxpayerName || '').trim();
  const kraNameMatches = doNamesReluctantlyMatch(user?.name || '', kraTaxpayerName);

  if (kraPin) {
    if (!kraNameMatches) {
      return {
        label: 'No match',
        icon: 'shield-x',
        className: 'border-red-200 bg-red-50 text-red-700',
      };
    }
    return {
      label: 'Verified',
      icon: 'shield-check',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    };
  }

  if (user?.kraPinCheckedAt) {
    return {
      label: 'Not verified',
      icon: 'shield-x',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
    };
  }

  return {
    label: 'Pending',
    icon: 'clock',
    className: 'border-slate-200 bg-slate-50 text-slate-600',
  };
};

const renderAdminUserKraSummaryWithOverride = (user) => {
  const kraPin = String(user?.kraPin || '').trim();
  const kraTaxpayerName = String(user?.kraTaxpayerName || '').trim();
  const kraNameMatches = doNamesReluctantlyMatch(user?.name || '', kraTaxpayerName);
  const kraNameOverride = user?.kraNameMatchOverridden === true;

  if (kraPin) {
    if (kraNameMatches) {
      return `PIN: ${maskKraPin(kraPin)} • ${kraTaxpayerName || 'Taxpayer name unavailable'}`;
    }
    if (kraNameOverride) {
      return `PIN: ${maskKraPin(kraPin)} • ${kraTaxpayerName || 'Taxpayer name unavailable'} • Match overridden`;
    }
    return `PIN: ${maskKraPin(kraPin)} • ${kraTaxpayerName || 'Taxpayer name unavailable'} • No match`;
  }

  if (user?.kraPinCheckedAt) {
    return 'User not verified';
  }

  return 'Verification pending';
};

const hasAdminUserKraNameMismatchWithOverride = (user) => {
  const kraPin = String(user?.kraPin || '').trim();
  const kraTaxpayerName = String(user?.kraTaxpayerName || '').trim();
  return !!kraPin && !doNamesReluctantlyMatch(user?.name || '', kraTaxpayerName) && user?.kraNameMatchOverridden !== true;
};

const getAdminUserKraStatusWithOverride = (user) => {
  const kraPin = String(user?.kraPin || '').trim();
  const kraTaxpayerName = String(user?.kraTaxpayerName || '').trim();
  const kraNameMatches = doNamesReluctantlyMatch(user?.name || '', kraTaxpayerName);
  const kraNameOverride = user?.kraNameMatchOverridden === true;

  if (kraPin) {
    if (kraNameOverride) {
      return {
        label: 'Verified override',
        icon: 'shield-check',
        className: 'border-amber-200 bg-amber-50 text-amber-700',
      };
    }
    if (!kraNameMatches) {
      return {
        label: 'No match',
        icon: 'shield-x',
        className: 'border-red-200 bg-red-50 text-red-700',
      };
    }
    return {
      label: 'Verified',
      icon: 'shield-check',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    };
  }

  if (user?.kraPinCheckedAt) {
    return {
      label: 'Not verified',
      icon: 'shield-x',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
    };
  }

  return {
    label: 'Pending',
    icon: 'clock',
    className: 'border-slate-200 bg-slate-50 text-slate-600',
  };
};

const UserRow = ({
  user,
  selectedModules,
  moduleOptions,
  isAdmin,
  isSaving,
  fallbackRole,
  onToggleModule,
  onSaveModules,
  onRetryKraVerification,
  onToggleKraMatchOverride,
  onEditProfile,
  onPromote,
  onToggleMpesaReconciliationAccess,
  onToggleMpesaReconciliationRecoverAccess,
  onToggleC2bPaymentBlock,
  onRemoveAdmin,
  onFallbackRoleChange,
  adminsCount
}) => (
  <tr className="align-top transition-all hover:bg-slate-50/90 group">
    <td className="px-3 py-3 border-b border-r border-slate-200" data-label="User">
      <div>
        <div className="text-sm font-semibold text-slate-900">{user.name}</div>
        <div className="text-xs text-slate-500 font-mono">{user.email}</div>
        <div className={`mt-1 text-[11px] ${hasAdminUserKraNameMismatchWithOverride(user) ? 'text-red-600' : 'text-slate-500'}`}>{renderAdminUserKraSummaryWithOverride(user)}</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600">
            <Icon name="id-card" size="10px" /> ID {user.idNumber}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
            <Icon name="log-in" size="10px" /> Logins {Number(user.loginCount || 0)}
          </span>
          {user.locked && (
            <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-600">
              <Icon name="lock" size="10px" /> Locked
            </span>
          )}
          {user.c2bPaymentBlocked && (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
              <Icon name="slash-circle" size="10px" /> C2B blocked
            </span>
          )}
        </div>
        <div className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-1">
          <Icon name="phone" size="11px" /> {user.phone || 'No phone'}
        </div>
        <div className="mt-1 text-[10px] text-slate-400 flex items-center gap-1">
          <Icon name="clock" size="10px" /> Last login: {formatLastLogin(user.lastLoginAt)}
        </div>
      </div>
    </td>
    <td className="px-3 py-3 border-b border-r border-slate-200" data-label="Role">
      <span
        className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
          isAdmin ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-slate-50 text-slate-700'
        }`}
      >
        {getRoleLabel(user.role)}
      </span>
      {isAdmin && <div className="mt-1.5 text-[10px] leading-4 text-slate-400">Admin account</div>}
    </td>
    <td className="px-3 py-3 border-b border-r border-slate-200" data-label="Modules">
      <ModuleBadges modules={selectedModules} />
    </td>
    <td className="px-3 py-3 border-b border-r border-slate-200" data-label="Recent Activity">
      <div className="space-y-2">
        <div className="text-xs font-medium text-slate-700">Last login: {formatLastLogin(user.lastLoginAt)}</div>
        <div className="space-y-1">
          {formatRecentLogins(user.recentLogins).length > 0 ? (
            formatRecentLogins(user.recentLogins).map((loginAt, index) => (
              <div key={`${user._id}-recent-${index}`} className="text-[11px] text-slate-500">
                {index + 1}. {loginAt}
              </div>
            ))
          ) : (
            <div className="text-[11px] text-slate-400">No recent login history yet.</div>
          )}
        </div>
      </div>
    </td>
    <td className="px-3 py-3 border-b border-slate-200" data-label="Actions">
      <UserActions
        user={user}
        selectedModules={selectedModules}
        moduleOptions={moduleOptions}
        isAdmin={isAdmin}
        isSaving={isSaving}
        fallbackRole={fallbackRole}
        onToggleModule={onToggleModule}
        onEditProfile={onEditProfile}
        onSaveModules={onSaveModules}
        onRetryKraVerification={onRetryKraVerification}
        onToggleKraMatchOverride={onToggleKraMatchOverride}
        onPromote={onPromote}
        onToggleMpesaReconciliationAccess={onToggleMpesaReconciliationAccess}
        onToggleMpesaReconciliationRecoverAccess={onToggleMpesaReconciliationRecoverAccess}
        onToggleC2bPaymentBlock={onToggleC2bPaymentBlock}
        onRemoveAdmin={onRemoveAdmin}
        onFallbackRoleChange={onFallbackRoleChange}
        adminsCount={adminsCount}
      />
    </td>
  </tr>
);

const MobileUserCard = ({
  user,
  selectedModules,
  moduleOptions,
  isAdmin,
  isSaving,
  fallbackRole,
  isExpanded,
  onToggleExpand,
  onToggleModule,
  onSaveModules,
  onRetryKraVerification,
  onToggleKraMatchOverride,
  onEditProfile,
  onPromote,
  onToggleMpesaReconciliationAccess,
  onToggleMpesaReconciliationRecoverAccess,
  onToggleC2bPaymentBlock,
  onRemoveAdmin,
  onFallbackRoleChange,
  adminsCount
}) => {
  const kraStatus = getAdminUserKraStatusWithOverride(user);

  return (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
    <button
      type="button"
      onClick={onToggleExpand}
      className="flex w-full items-start justify-between gap-3 px-4 py-4 text-left"
      aria-expanded={isExpanded}
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900">{user.name}</div>
        <div className="truncate text-xs text-slate-500 font-mono">{user.email}</div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold ${kraStatus.className}`}>
            <Icon name={kraStatus.icon} size="10px" />
            {kraStatus.label}
          </span>
        </div>
        <div className={`mt-1 truncate text-[11px] ${hasAdminUserKraNameMismatchWithOverride(user) ? 'text-red-600' : 'text-slate-500'}`}>{renderAdminUserKraSummaryWithOverride(user)}</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
            isAdmin ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-slate-50 text-slate-700'
          }`}>
            {getRoleLabel(user.role)}
          </span>
          <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            user.locked ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
          }`}>
            {user.locked ? 'Locked' : 'Active'}
          </span>
          {user.c2bPaymentBlocked && (
            <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
              C2B blocked
            </span>
          )}
        </div>
        <div className="mt-2 text-[11px] text-slate-500">ID {user.idNumber || 'N/A'}</div>
        <div className="text-[11px] text-slate-500">{user.phone || 'No phone'}</div>
      </div>
      <div className="flex items-center gap-2 pt-0.5">
        <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-600">
          {selectedModules.length} module{selectedModules.length === 1 ? '' : 's'}
        </span>
        <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size="16px" className="text-slate-500" />
      </div>
    </button>

    {isExpanded && (
      <div className="border-t border-slate-200 px-4 py-4">
        <div className="mb-3 space-y-1 text-[11px] text-slate-500">
          <div>Last login: {formatLastLogin(user.lastLoginAt)}</div>
          {formatRecentLogins(user.recentLogins).map((loginAt, index) => (
            <div key={`${user._id}-mobile-recent-${index}`}>{index + 1}. {loginAt}</div>
          ))}
        </div>
        <UserActions
          user={user}
          selectedModules={selectedModules}
          moduleOptions={moduleOptions}
          isAdmin={isAdmin}
          isSaving={isSaving}
          fallbackRole={fallbackRole}
          onToggleModule={onToggleModule}
          onEditProfile={onEditProfile}
          onSaveModules={onSaveModules}
          onRetryKraVerification={onRetryKraVerification}
          onToggleKraMatchOverride={onToggleKraMatchOverride}
          onPromote={onPromote}
          onToggleMpesaReconciliationAccess={onToggleMpesaReconciliationAccess}
          onToggleMpesaReconciliationRecoverAccess={onToggleMpesaReconciliationRecoverAccess}
          onToggleC2bPaymentBlock={onToggleC2bPaymentBlock}
          onRemoveAdmin={onRemoveAdmin}
          onFallbackRoleChange={onFallbackRoleChange}
          adminsCount={adminsCount}
        />
      </div>
    )}
  </div>
  );
};

// Main Component
function AdminUsersPage() {
  const navigate = useNavigate();
  const { currentUser, loading } = useAuth(navigate);
  const {
    admins,
    users,
    pageLoading,
    error: fetchError,
    moduleSelections,
    fallbackRoles,
    setAdmins,
    setUsers,
    setModuleSelections,
    setFallbackRoles,
    setError: setFetchError,
  } = useDirectoryData(currentUser);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [savingUserId, setSavingUserId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [bulkScope, setBulkScope] = useState('all');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkModuleActionKey, setBulkModuleActionKey] = useState('');
  const [bulkVerifySaving, setBulkVerifySaving] = useState(false);
  const [kraRegistrationVerificationEnabled, setKraRegistrationVerificationEnabled] = useState(true);
  const [kraToggleSaving, setKraToggleSaving] = useState(false);
  
  const searchInputRef = useRef(null);
  const autoVerifyAttemptedIdsRef = useRef(new Set());

  const moduleOptions = useMemo(() => {
    const discoveredModules = new Set(BASE_MODULE_OPTIONS);

    users.forEach((user) => {
      (user.assignedModules || []).forEach((moduleName) => {
        const normalized = String(moduleName || '').trim();
        if (normalized) discoveredModules.add(normalized);
      });
    });

    admins.forEach((admin) => {
      (admin.assignedModules || []).forEach((moduleName) => {
        const normalized = String(moduleName || '').trim();
        if (normalized) discoveredModules.add(normalized);
      });
    });

    Object.values(moduleSelections).forEach((modules) => {
      (modules || []).forEach((moduleName) => {
        const normalized = String(moduleName || '').trim();
        if (normalized) discoveredModules.add(normalized);
      });
    });

    return sortModuleNames(Array.from(discoveredModules));
  }, [admins, moduleSelections, users]);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const sorted = [...users].sort((a, b) => {
      if (a.role === 'A' && b.role !== 'A') return -1;
      if (a.role !== 'A' && b.role === 'A') return 1;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });

    const roleFiltered = roleFilter === 'all'
      ? sorted
      : sorted.filter((user) => String(user.role || '') === roleFilter);

    if (!query) return roleFiltered;

    return roleFiltered.filter((user) => {
      const haystack = [
        user.name,
        user.email,
        user.phone,
        user.idNumber,
        user.role,
        ...(user.assignedModules || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [roleFilter, searchQuery, users]);
  const moduleAccessSummary = useMemo(() => {
    const totalUsers = users.length;

    return moduleOptions.map((moduleName) => {
      const assignedCount = users.reduce((count, user) => {
        const assignedModules = moduleSelections[user._id] || [];
        return assignedModules.includes(moduleName) ? count + 1 : count;
      }, 0);

      return {
        moduleName,
        assignedCount,
        totalUsers,
        unassignedCount: Math.max(totalUsers - assignedCount, 0),
        allAssigned: totalUsers > 0 && assignedCount === totalUsers,
        noneAssigned: assignedCount === 0,
      };
    });
  }, [moduleOptions, moduleSelections, users]);

  // Handlers (all original functions preserved)
  const handleToggleModule = useCallback((userId, moduleName) => {
    setModuleSelections((prev) => {
      const current = prev[userId] || [];
      const next = current.includes(moduleName)
        ? current.filter((m) => m !== moduleName)
        : [...current, moduleName];
      return { ...prev, [userId]: next };
    });
  }, [setModuleSelections]);

  const handleFallbackRoleChange = useCallback((userId, roleValue) => {
    setFallbackRoles((prev) => ({
      ...prev,
      [userId]: roleValue,
    }));
  }, [setFallbackRoles]);

  const refreshDirectoryState = useCallback((updatedUser) => {
    setUsers((prev) =>
      prev.map((user) =>
        user._id === updatedUser._id ? { ...user, ...updatedUser } : user
      )
    );

    setAdmins((prev) => {
      const exists = prev.some((admin) => admin._id === updatedUser._id);
      if (exists) {
        return prev.map((admin) =>
          admin._id === updatedUser._id ? { ...admin, ...updatedUser } : admin
        );
      }
      return [...prev, updatedUser].sort((a, b) => 
        String(a.name || '').localeCompare(String(b.name || ''))
      );
    });
  }, [setUsers, setAdmins]);

  const refreshDirectoryStates = useCallback((updatedUsers) => {
    if (!Array.isArray(updatedUsers) || updatedUsers.length === 0) return;

    const updatesById = new Map(
      updatedUsers.map((entry) => [String(entry._id), entry])
    );

    setUsers((prev) => prev.map((user) => updatesById.get(String(user._id)) || user));
    setAdmins((prev) => prev.map((user) => updatesById.get(String(user._id)) || user));
  }, [setUsers, setAdmins]);

  const showMessage = useCallback((msg, isError = false) => {
    if (isError) {
      setError(msg);
      setMessage('');
    } else {
      setMessage(msg);
      setError('');
    }
    setTimeout(() => {
      setMessage('');
      setError('');
    }, MESSAGE_DURATION);
  }, []);

  const fetchNotificationSettings = useCallback(async () => {
    try {
      const res = await api.get('/frp/admin/bulk-invoice-notification-settings');
      const settings = res?.data?.settings || {};
      setKraRegistrationVerificationEnabled(settings?.kraRegistrationVerificationEnabled !== false);
    } catch (settingsError) {
      console.error('Failed to fetch notification settings:', settingsError);
    }
  }, []);

  const handleKraRegistrationToggle = useCallback(async (nextValue) => {
    const previousValue = kraRegistrationVerificationEnabled;
    setKraRegistrationVerificationEnabled(nextValue);
    setKraToggleSaving(true);

    try {
      const res = await api.put('/frp/admin/bulk-invoice-notification-settings', {
        kraRegistrationVerificationEnabled: nextValue,
      });
      const settings = res?.data?.settings || {};
      setKraRegistrationVerificationEnabled(settings?.kraRegistrationVerificationEnabled !== false);
      showMessage(`KRA registration verification ${settings?.kraRegistrationVerificationEnabled !== false ? 'enabled' : 'disabled'}.`);
    } catch (toggleError) {
      console.error('Failed to save KRA registration verification setting:', toggleError);
      setKraRegistrationVerificationEnabled(previousValue);
      showMessage(toggleError?.response?.data?.error || 'Failed to save KRA verification setting.', true);
    } finally {
      setKraToggleSaving(false);
    }
  }, [kraRegistrationVerificationEnabled, showMessage]);

  const handleSaveModules = useCallback(async (user) => {
    try {
      setSavingUserId(user._id);
      
      const assignedModules = moduleSelections[user._id] || [];
      const res = await api.put(`/auth/users/${user._id}/module-access`, {
        assignedModules,
      });

      const updatedUser = res?.data?.user;
      if (updatedUser) {
        refreshDirectoryState(updatedUser);
      }

      if (res?.data?.token && user._id === currentUser?._id) {
        localStorage.setItem('token', res.data.token);
      }

      showMessage(`Module access updated for ${user.name || user.email || 'user'}.`);
    } catch (saveError) {
      console.error('Error updating module access:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to update module access', true);
    } finally {
      setSavingUserId(null);
    }
  }, [moduleSelections, refreshDirectoryState, currentUser, showMessage]);

  const handleBulkModuleAccessUpdate = useCallback(async (moduleName, enabled) => {
    const actionLabel = enabled ? 'allow' : 'disallow';
    const moduleLabel = getModuleLabel(moduleName);
    const confirmMessage = enabled
      ? `Allow all users to access ${moduleLabel}? This will add ${moduleLabel} to every user's assigned modules.`
      : `Disallow all users from accessing ${moduleLabel}? This will remove ${moduleLabel} from every user's assigned modules.`;

    if (!window.confirm(confirmMessage)) return;

    const actionKey = `${moduleName}:${enabled ? 'allow' : 'disallow'}`;
    try {
      setBulkModuleActionKey(actionKey);

      const res = await api.put('/auth/users/module-access/bulk', {
        moduleName,
        enabled,
      });

      const updatedUsers = Array.isArray(res?.data?.users) ? res.data.users : [];
      if (updatedUsers.length > 0) {
        setUsers(updatedUsers);
        setAdmins(updatedUsers.filter((user) => user.role === 'A'));
      }

      if (res?.data?.moduleSelections && typeof res.data.moduleSelections === 'object') {
        setModuleSelections(res.data.moduleSelections);
      } else if (updatedUsers.length > 0) {
        setModuleSelections(buildModuleSelectionState(updatedUsers));
      }

      if (res?.data?.token) {
        localStorage.setItem('token', res.data.token);
      }

      showMessage(res?.data?.message || `${moduleLabel} access updated for all users.`);
    } catch (saveError) {
      console.error(`Error running bulk ${actionLabel} for module access:`, saveError);
      showMessage(saveError?.response?.data?.error || `Failed to ${actionLabel} ${moduleLabel} for all users.`, true);
    } finally {
      setBulkModuleActionKey('');
    }
  }, [setAdmins, setModuleSelections, setUsers, showMessage]);

  const handleRetryKraVerification = useCallback(async (user) => {
    try {
      setSavingUserId(user._id);

      const res = await api.post('/kra/pin-lookup', {
        userId: user._id,
        force: true,
      });

      const updatedUser = res?.data?.user;
      if (updatedUser) {
        refreshDirectoryState(updatedUser);
      }

      if (res?.data?.verified) {
        showMessage(`KRA verification updated for ${user.name || user.email || 'user'}.`);
      } else {
        showMessage(`${user.name || user.email || 'User'} is still not verified on KRA.`, true);
      }
    } catch (saveError) {
      console.error('Error retrying KRA verification:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to retry KRA verification', true);
    } finally {
      setSavingUserId(null);
    }
  }, [refreshDirectoryState, showMessage]);

  const handleToggleKraMatchOverride = useCallback(async (user) => {
    try {
      setSavingUserId(user._id);

      const enabled = !(user?.kraNameMatchOverridden === true);
      const res = await api.put(`/auth/users/${user._id}/kra-name-match-override`, {
        enabled,
      });

      const updatedUser = res?.data?.user;
      if (updatedUser) {
        refreshDirectoryState(updatedUser);
      }

      showMessage(
        enabled
          ? `KRA name match override enabled for ${user.name || user.email || 'user'}.`
          : `KRA name match override removed for ${user.name || user.email || 'user'}.`
      );
    } catch (saveError) {
      console.error('Error updating KRA match override:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to update KRA match override', true);
    } finally {
      setSavingUserId(null);
    }
  }, [refreshDirectoryState, showMessage]);

  const handleBulkVerify = useCallback(async () => {
    const targets = filteredUsers.filter((user) => !String(user?.kraPin || '').trim());

    if (targets.length === 0) {
      showMessage('No unverified users found in the current view.');
      return;
    }

    try {
      setBulkVerifySaving(true);

      const updatedUsers = [];
      const concurrency = 4;

      for (let index = 0; index < targets.length; index += concurrency) {
        const batch = targets.slice(index, index + concurrency);
        const results = await Promise.allSettled(
          batch.map((user) =>
            api.post('/kra/pin-lookup', {
              userId: user._id,
              force: true,
            })
          )
        );

        results.forEach((result, batchIndex) => {
          const targetUser = batch[batchIndex];
          if (result.status === 'fulfilled' && result.value?.data?.user) {
            updatedUsers.push(result.value.data.user);
            autoVerifyAttemptedIdsRef.current.add(String(targetUser?._id || ''));
            return;
          }

          console.error(
            `[KRA][BULK_VERIFY_FAILED] userId=${targetUser?._id || 'n/a'} idNumber=${targetUser?.idNumber || 'n/a'}`,
            result.reason?.response?.data || result.reason?.message || result.reason
          );
        });
      }

      if (updatedUsers.length > 0) {
        refreshDirectoryStates(updatedUsers);
      }

      const verifiedCount = updatedUsers.filter((user) => String(user?.kraPin || '').trim()).length;
      const failedCount = targets.length - updatedUsers.length;
      showMessage(
        `Bulk verify finished. Verified ${verifiedCount} of ${targets.length} users${failedCount > 0 ? `, ${failedCount} requests failed` : ''}.`
      );
    } catch (saveError) {
      console.error('Error running bulk KRA verification:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to run bulk KRA verification', true);
    } finally {
      setBulkVerifySaving(false);
    }
  }, [filteredUsers, refreshDirectoryStates, showMessage]);

  const handlePromote = useCallback(async (user) => {
    try {
      setSavingUserId(user._id);
      
      const assignedModules = moduleSelections[user._id] || [];
      const res = await api.put(`/auth/users/${user._id}/admin-access`, {
        assignedModules,
      });

      const updatedUser = res?.data?.user;
      if (updatedUser) {
        refreshDirectoryState(updatedUser);
        setModuleSelections((prev) => ({
          ...prev,
          [user._id]: updatedUser.assignedModules || [],
        }));
      }

      if (res?.data?.token && user._id === currentUser?._id) {
        localStorage.setItem('token', res.data.token);
      }

      showMessage(`${user.name || user.email || 'User'} can now sign in as an admin.`);
    } catch (saveError) {
      console.error('Error updating admin access:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to update admin access', true);
    } finally {
      setSavingUserId(null);
    }
  }, [moduleSelections, refreshDirectoryState, currentUser, showMessage, setModuleSelections]);

  const handleRemoveAdmin = useCallback(async (user) => {
    const fallbackRole = fallbackRoles[user._id] || 'Mbr';
    const fallbackLabel = getRoleLabel(fallbackRole);
    
    if (!window.confirm(
      `Remove admin access for ${user.name || user.email || 'this user'} and change their role to ${fallbackLabel}?`
    )) return;

    try {
      setSavingUserId(user._id);
      
      const res = await api.put(`/auth/users/${user._id}/remove-admin-access`, {
        fallbackRole,
      });

      const updatedUser = res?.data?.user;
      if (updatedUser) {
        setUsers((prev) =>
          prev.map((entry) =>
            entry._id === updatedUser._id ? { ...entry, ...updatedUser } : entry
          )
        );
        setAdmins((prev) => prev.filter((admin) => admin._id !== updatedUser._id));
        setModuleSelections((prev) => ({
          ...prev,
          [user._id]: [],
        }));
      }

      if (res?.data?.token && user._id === currentUser?._id) {
        localStorage.setItem('token', res.data.token);
        navigate('/dashboard');
        return;
      }

      showMessage(`${user.name || user.email || 'User'} is no longer an admin.`);
    } catch (saveError) {
      console.error('Error removing admin access:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to remove admin access', true);
    } finally {
      setSavingUserId(null);
    }
  }, [fallbackRoles, currentUser, navigate, setUsers, setAdmins, setModuleSelections, showMessage]);

  const handleToggleMpesaReconciliationAccess = useCallback(async (user) => {
    try {
      setSavingUserId(user._id);
      const enabled = !user.canUseMpesaReconciliation;
      const res = await api.put(`/auth/users/${user._id}/mpesa-reconciliation-access`, { enabled });
      const updatedUser = res?.data?.user;
      if (updatedUser) {
        refreshDirectoryState(updatedUser);
      }
      showMessage(
        `${user.name || user.email || 'User'} ${enabled ? 'can now search' : 'can no longer search'} M-Pesa reconciliation.`
      );
    } catch (saveError) {
      console.error('Error updating reconciliation access:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to update reconciliation access', true);
    } finally {
      setSavingUserId(null);
    }
  }, [refreshDirectoryState, showMessage]);

  const handleToggleMpesaReconciliationRecoverAccess = useCallback(async (user) => {
    try {
      setSavingUserId(user._id);
      const enabled = !user.canRecoverMpesaReconciliation;
      const res = await api.put(`/auth/users/${user._id}/mpesa-reconciliation-recover-access`, { enabled });
      const updatedUser = res?.data?.user;
      if (updatedUser) {
        refreshDirectoryState(updatedUser);
      }
      showMessage(
        `${user.name || user.email || 'User'} ${enabled ? 'can now reconcile' : 'can no longer reconcile'} M-Pesa transactions.`
      );
    } catch (saveError) {
      console.error('Error updating reconciliation recovery access:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to update reconciliation recovery access', true);
    } finally {
      setSavingUserId(null);
    }
  }, [refreshDirectoryState, showMessage]);

  const handleToggleC2bPaymentBlock = useCallback(async (user) => {
    const blocked = !user.c2bPaymentBlocked;
    const userLabel = user.name || user.email || 'this user';
    if (blocked && !window.confirm(
      `Block C2B payments for ${userLabel}? M-Pesa validation requests using ID ${user.idNumber || 'N/A'} will be rejected.`
    )) return;

    try {
      setSavingUserId(user._id);
      const res = await api.put(`/auth/users/${user._id}/c2b-payment-block`, { blocked });
      const updatedUser = res?.data?.user;
      if (updatedUser) {
        refreshDirectoryState(updatedUser);
      }
      showMessage(`C2B payments ${blocked ? 'blocked for' : 'allowed for'} ${userLabel}.`);
    } catch (saveError) {
      console.error('Error updating C2B payment block:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to update C2B payment access', true);
    } finally {
      setSavingUserId(null);
    }
  }, [refreshDirectoryState, showMessage]);

  const handleOpenEditProfile = useCallback((user) => {
    setEditingUser(user);
  }, []);

  const handleProfileSaved = useCallback((updatedUser, successMessage) => {
    if (updatedUser) {
      refreshDirectoryState(updatedUser);
    }
    setEditingUser(null);
    showMessage(successMessage || 'User profile updated successfully');
  }, [refreshDirectoryState, showMessage]);

  const handleBulkMpesaAccessUpdate = useCallback(async (accessType, enabled) => {
    const scopeLabels = {
      all: 'all users',
      management: 'all agents',
      tenants: 'all tenants',
    };

    try {
      setBulkSaving(true);
      const res = await api.put('/auth/users/mpesa-reconciliation-bulk', {
        scope: bulkScope,
        accessType,
        enabled,
      });

      refreshDirectoryStates(res?.data?.users || []);

      const actionLabel = accessType === 'recover'
        ? (enabled ? 'enabled reconcile for' : 'disabled reconcile for')
        : (enabled ? 'enabled search for' : 'disabled search for');

      showMessage(`M-Pesa reconciliation: ${actionLabel} ${scopeLabels[bulkScope] || 'selected users'}.`);
    } catch (saveError) {
      console.error('Error updating bulk reconciliation access:', saveError);
      showMessage(saveError?.response?.data?.error || 'Failed to update bulk reconciliation access', true);
    } finally {
      setBulkSaving(false);
    }
  }, [bulkScope, refreshDirectoryStates, showMessage]);

  const clearMessages = useCallback(() => {
    setMessage('');
    setError('');
    setFetchError('');
  }, [setFetchError]);

  const reconciliationSearchUsersCount = useMemo(
    () => users.filter((user) => user.canUseMpesaReconciliation).length,
    [users]
  );
  const reconciliationRecoverUsersCount = useMemo(
    () => users.filter((user) => user.canRecoverMpesaReconciliation).length,
    [users]
  );
  const activeUsersCount = useMemo(
    () => users.filter((user) => isUserRecentlyActive(user)).length,
    [users]
  );
  const todayLoginsCount = useMemo(() => {
    const today = new Date();
    return users.reduce((count, user) => {
      const recentLogins = Array.isArray(user.recentLogins) ? user.recentLogins : [];
      return count + recentLogins.filter((loginAt) => isSameLocalDay(loginAt, today)).length;
    }, 0);
  }, [users]);
  const usersWithMpesaAccessCount = useMemo(
    () => users.filter((user) => user.canUseMpesaReconciliation || user.canRecoverMpesaReconciliation).length,
    [users]
  );
  const recentActivityItems = useMemo(() => {
    const activities = [];

    users.forEach((user) => {
      const userLabel = user.name || user.email || 'Unknown user';
      const lastSeenMs = getTimestampMs(user.lastSeenAt);
      const lastLoginMs = getTimestampMs(user.lastLoginAt);
      const createdAtMs = getTimestampMs(user.createdAt);

      if (lastSeenMs && (!lastLoginMs || Math.abs(lastSeenMs - lastLoginMs) > 60 * 1000)) {
        activities.push({
          id: `seen-${user._id}-${lastSeenMs}`,
          at: lastSeenMs,
          icon: 'clock',
          title: `${userLabel} was active`,
          detail: `${formatRelativeTime(user.lastSeenAt)} - Seen ${formatLastLogin(user.lastSeenAt)}`,
        });
      }

      if (lastLoginMs) {
        activities.push({
          id: `login-${user._id}-${lastLoginMs}`,
          at: lastLoginMs,
          icon: 'log-in',
          title: `${userLabel} logged in`,
          detail: `${formatRelativeTime(user.lastLoginAt)} - ${formatLastLogin(user.lastLoginAt)}`,
        });
      }

      if (createdAtMs) {
        activities.push({
          id: `created-${user._id}-${createdAtMs}`,
          at: createdAtMs,
          icon: 'person-plus',
          title: `${userLabel} was added`,
          detail: `${formatRelativeTime(user.createdAt)} - ${formatLastLogin(user.createdAt)}`,
        });
      }
    });

    return activities
      .sort((a, b) => b.at - a.at)
      .slice(0, 5);
  }, [users]);
  const bulkScopeUserCount = useMemo(() => {
    if (bulkScope === 'management') return users.filter((user) => user.role === 'M').length;
    if (bulkScope === 'tenants') return users.filter((user) => user.role === 'T').length;
    return users.filter((user) => user.role !== 'A').length;
  }, [bulkScope, users]);

  useEffect(() => {
    if (!expandedUserId) return;
    const stillVisible = filteredUsers.some((user) => user._id === expandedUserId);
    if (!stillVisible) {
      setExpandedUserId(null);
    }
  }, [expandedUserId, filteredUsers]);

  useEffect(() => {
    if (!currentUser?._id) return;
    fetchNotificationSettings();
  }, [currentUser, fetchNotificationSettings]);

  // Loading state
  if (loading) return <LoadingSpinner />;
  if (!currentUser) return null;

  return (
    <AdminShell
      user={currentUser}
      title="Management"
      subtitle="Permissions, modules, registration controls, and SMS settings"
      maxWidth="max-w-7xl"
    >
      <div className="space-y-5">
        {/* Header Section - Excel style summary cards */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Icon name="grid" size="18px" className="text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Operations Dashboard</h3>
                <p className="text-xs text-slate-500">Module, presence & reconciliation overview</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {moduleOptions.map((moduleName) => (
                <span
                  key={moduleName}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm"
                >
                  {getModuleLabel(moduleName)}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-3 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    Active Users
                  </div>
                  <div className="mt-1 text-2xl font-bold text-slate-800">{activeUsersCount}</div>
                  <div className="mt-1 text-[11px] text-slate-500">Seen in the last 2 minutes</div>
                  <div className="mt-2 text-xs font-medium text-slate-600">Today logins: {todayLoginsCount}</div>
                </div>
                <div className="w-full rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 sm:w-auto sm:min-w-[15rem]">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                    KRA Verification
                  </div>
                  <label className="mt-2 inline-flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={kraRegistrationVerificationEnabled}
                      onChange={(e) => handleKraRegistrationToggle(e.target.checked)}
                      disabled={kraToggleSaving}
                    />
                    <div className="relative h-5 w-10 rounded-full bg-gray-200 transition-colors peer-checked:bg-emerald-600">
                      <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all peer-checked:left-5" />
                    </div>
                    <span className="ml-2 text-xs font-medium text-slate-700">
                      {kraToggleSaving ? 'Saving...' : kraRegistrationVerificationEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-3 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Icon name="search" size="12px" />
                    M-Pesa Access
                  </div>
                  <div className="mt-1 text-2xl font-bold text-slate-800">{usersWithMpesaAccessCount}</div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    Search: {reconciliationSearchUsersCount} | Reconcile: {reconciliationRecoverUsersCount}
                  </div>
                </div>
                <div className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-600">
                  Scope users: {bulkScopeUserCount}
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,11rem)_repeat(2,minmax(0,1fr))]">
                <select
                  value={bulkScope}
                  onChange={(e) => setBulkScope(e.target.value)}
                  disabled={bulkSaving}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <option value="all">All users</option>
                  <option value="management">Agents</option>
                  <option value="tenants">Tenants</option>
                </select>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleBulkMpesaAccessUpdate('search', true)}
                    disabled={bulkSaving}
                    className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Enable Search
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkMpesaAccessUpdate('search', false)}
                    disabled={bulkSaving}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Disable Search
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleBulkMpesaAccessUpdate('recover', true)}
                    disabled={bulkSaving}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Enable Reconcile
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkMpesaAccessUpdate('recover', false)}
                    disabled={bulkSaving}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Disable Reconcile
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <Icon name="clock" size="12px" />
                  Activity Log
                </div>
                <div className="mt-1 text-[11px] text-slate-500">5 most recent user activities from the current directory data</div>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-600">
                Latest 5
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {recentActivityItems.length > 0 ? (
                recentActivityItems.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
                  >
                    <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                      <Icon name={activity.icon} size="14px" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800">{activity.title}</div>
                      <div className="text-[11px] text-slate-500">{activity.detail}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500">
                  No recent activity data is available yet.
                </div>
              )}
            </div>
          </div>

          <p className="mt-3 text-xs leading-5 text-slate-500 border-t border-slate-100 pt-3">
            <Icon name="info" size="12px" className="inline mr-1" />
            Use the toggles below to decide whether each agent can only search or can also reconcile with their own PIN. Bulk controls affect agents, tenants, or all non-admin users.
          </p>
        </div>

        {/* Message Alerts */}
        <AlertMessage message={message} error={error || fetchError} onDismiss={clearMessages} />

        <AdminBulkInvoiceSmsSettingsPanel />

        <section className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="flex flex-col gap-2 border-b border-slate-200 bg-[#fafcfd] px-5 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Icon name="grid" size="18px" /> Module Access For All Users
              </h2>
              <p className="text-xs text-slate-500">
                Bulk actions update each user's existing <span className="font-mono">assignedModules</span> without removing individual control afterward.
              </p>
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm">
              {users.length} total user{users.length === 1 ? '' : 's'}
            </div>
          </div>

          <div className="grid gap-3 p-4 md:grid-cols-3">
            {moduleAccessSummary.map(({ moduleName, assignedCount, totalUsers, unassignedCount, allAssigned, noneAssigned }) => {
              const allowActionKey = `${moduleName}:allow`;
              const disallowActionKey = `${moduleName}:disallow`;
              const isAllowing = bulkModuleActionKey === allowActionKey;
              const isDisallowing = bulkModuleActionKey === disallowActionKey;
              const isWorking = isAllowing || isDisallowing;

              return (
                <div key={moduleName} className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-slate-800">{getModuleLabel(moduleName)}</div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {assignedCount} of {totalUsers} users assigned
                      </div>
                    </div>
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                        allAssigned
                          ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                          : noneAssigned
                            ? 'border border-slate-200 bg-slate-100 text-slate-600'
                            : 'border border-amber-200 bg-amber-50 text-amber-700'
                      }`}
                    >
                      {allAssigned ? 'All assigned' : noneAssigned ? 'None assigned' : 'Partially assigned'}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => handleBulkModuleAccessUpdate(moduleName, true)}
                      disabled={isWorking || allAssigned}
                      className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isAllowing ? 'Applying...' : 'Allow All'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkModuleAccessUpdate(moduleName, false)}
                      disabled={isWorking || noneAssigned}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isDisallowing ? 'Applying...' : 'Disallow All'}
                    </button>
                  </div>

                  <div className="mt-3 text-[11px] text-slate-500">
                    {unassignedCount === 0
                      ? 'Every user currently has this module.'
                      : `${unassignedCount} user${unassignedCount === 1 ? '' : 's'} can still be assigned individually later.`}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {false && (
        <section className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-[#fafcfd] px-5 py-4">
            <div>
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Icon name="shield-user" size="18px" /> Current Admins
              </h2>
              <p className="text-xs text-slate-500">Admin accounts with full module access</p>
            </div>
            <div className="rounded-full bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700 shadow-sm">
              {admins.length} admin{admins.length === 1 ? '' : 's'}
            </div>
          </div>

          {pageLoading ? (
            <div className="py-12 text-center text-gray-500">Loading admins...</div>
          ) : admins.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 m-5 px-4 py-8 text-center text-sm text-slate-500">
              No admin accounts were found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="excel-table w-full text-sm">
                <thead className="bg-[#f8fafc]">
                  <tr>
                    <ExcelTableHeader>Admin</ExcelTableHeader>
                    <ExcelTableHeader>Status</ExcelTableHeader>
                    <ExcelTableHeader>Modules</ExcelTableHeader>
                    <ExcelTableHeader>Last Login</ExcelTableHeader>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {admins.map((admin) => (
                    <tr key={admin._id} className="hover:bg-slate-50/80 transition">
                      <td className="px-3 py-3 border-b border-r border-slate-200" data-label="Admin">
                        <div className="font-semibold text-slate-800">{admin.name}</div>
                        <div className="text-xs text-slate-500 font-mono">{admin.email}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                            ID {admin.idNumber}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                            Logins {admin.loginCount || 0}
                          </span>
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">{admin.phone || '—'}</div>
                      </td>
                      <td className="px-3 py-3 border-b border-r border-slate-200" data-label="Status">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          admin.locked ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                        }`}>
                          {admin.locked ? 'Locked' : 'Active'}
                        </span>
                      </td>
                      <td className="px-3 py-3 border-b border-r border-slate-200" data-label="Modules">
                        <div className="flex flex-wrap gap-1.5">
                          {(admin.assignedModules || []).length > 0 ? (
                            admin.assignedModules.map((mod) => (
                              <span key={mod} className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-700 border border-slate-200">
                                {mod}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-slate-400 italic">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 border-b border-slate-200 text-sm text-slate-600" data-label="Last Login">
                        {formatLastLogin(admin.lastLoginAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        )}

        {/* User Directory Section - Excel style with mobile optimization */}
        <section className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-b border-slate-200 bg-[#fafcfd] px-5 py-4">
            <div>
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Icon name="users" size="18px" /> User Directory
              </h2>
              <p className="text-xs text-slate-500">Search, assign permissions & manage admin access</p>
            </div>
            <div className="grid w-full gap-3 md:w-auto md:grid-cols-[minmax(0,11rem)_18rem]">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200 transition"
              >
                {ROLE_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="relative">
                <Icon name="search" size="14px" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  id="userSearch"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Name, email, phone, ID..."
                  ref={searchInputRef}
                  className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200 transition"
                />
              </div>
            </div>
            <div className="flex w-full md:w-auto">
              <button
                type="button"
                onClick={handleBulkVerify}
                disabled={bulkVerifySaving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
              >
                <Icon name="arrow-clockwise" size="14px" />
                {bulkVerifySaving ? 'Verifying...' : 'Bulk Verify'}
              </button>
            </div>
          </div>

          {pageLoading ? (
            <div className="py-12 text-center text-gray-500">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 m-5 px-4 py-10 text-center text-sm text-slate-500">
              No users matched your search.
            </div>
          ) : (
            <>
              <div className="space-y-3 p-4">
                {filteredUsers.map((user) => {
                  const selectedModules = moduleSelections[user._id] || [];
                  const isAdmin = user.role === 'A';
                  const isSaving = savingUserId === user._id;
                  const fallbackRole = fallbackRoles[user._id] || 'Mbr';

                  return (
                    <MobileUserCard
                      key={user._id}
                      user={user}
                      selectedModules={selectedModules}
                      moduleOptions={moduleOptions}
                      isAdmin={isAdmin}
                      isSaving={isSaving}
                      fallbackRole={fallbackRole}
                      isExpanded={expandedUserId === user._id}
                      onToggleExpand={() => setExpandedUserId((current) => (current === user._id ? null : user._id))}
                      onToggleModule={handleToggleModule}
                      onEditProfile={handleOpenEditProfile}
                      onSaveModules={handleSaveModules}
                      onRetryKraVerification={handleRetryKraVerification}
                      onToggleKraMatchOverride={handleToggleKraMatchOverride}
                      onPromote={handlePromote}
                      onToggleMpesaReconciliationAccess={handleToggleMpesaReconciliationAccess}
                      onToggleMpesaReconciliationRecoverAccess={handleToggleMpesaReconciliationRecoverAccess}
                      onToggleC2bPaymentBlock={handleToggleC2bPaymentBlock}
                      onRemoveAdmin={handleRemoveAdmin}
                      onFallbackRoleChange={handleFallbackRoleChange}
                      adminsCount={admins.length}
                    />
                  );
                })}
              </div>

              <div className="hidden overflow-x-auto">
                <table className="excel-table w-full text-sm">
                <thead className="bg-[#f8fafc]">
                  <tr>
                    <ExcelTableHeader>User</ExcelTableHeader>
                    <ExcelTableHeader>Role</ExcelTableHeader>
                    <ExcelTableHeader>Modules</ExcelTableHeader>
                    <ExcelTableHeader>Recent Activity</ExcelTableHeader>
                    <ExcelTableHeader>Actions</ExcelTableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    const selectedModules = moduleSelections[user._id] || [];
                    const isAdmin = user.role === 'A';
                    const isSaving = savingUserId === user._id;
                    const fallbackRole = fallbackRoles[user._id] || 'Mbr';

                    return (
                      <UserRow
                        key={user._id}
                        user={user}
                        selectedModules={selectedModules}
                        moduleOptions={moduleOptions}
                        isAdmin={isAdmin}
                        isSaving={isSaving}
                        fallbackRole={fallbackRole}
                        onToggleModule={handleToggleModule}
                        onEditProfile={handleOpenEditProfile}
                        onSaveModules={handleSaveModules}
                        onRetryKraVerification={handleRetryKraVerification}
                        onToggleKraMatchOverride={handleToggleKraMatchOverride}
                        onPromote={handlePromote}
                        onToggleMpesaReconciliationAccess={handleToggleMpesaReconciliationAccess}
                        onToggleMpesaReconciliationRecoverAccess={handleToggleMpesaReconciliationRecoverAccess}
                        onToggleC2bPaymentBlock={handleToggleC2bPaymentBlock}
                        onRemoveAdmin={handleRemoveAdmin}
                        onFallbackRoleChange={handleFallbackRoleChange}
                        adminsCount={admins.length}
                      />
                    );
                  })}
                </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
      <AdminUserProfileModal
        user={editingUser}
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
        onSaved={handleProfileSaved}
      />
    </AdminShell>
  );
}

export default AdminUsersPage;
